/**
 * The row cursor — what a keypress means, and where it lands.
 *
 * Pure, and in `lib/` rather than beside the grid for two reasons. This
 * package's rule is that `components/` renders and `lib/` does not, and more
 * concretely a module exporting both a component and plain functions breaks
 * React Fast Refresh — the same reason `event-source.ts` and `data-frames.ts`
 * were split out.
 *
 * Nothing here touches AG Grid. The grid supplies a row count and a current
 * index; these decide the rest, which is what makes the edges testable without
 * a DOM that has layout.
 */

/**
 * The part of a `KeyboardEvent` the cursor reads. Structural on purpose: a test
 * states a case as an object literal instead of staging a DOM event.
 */
/** Which way the cursor moves. */
export type RowStep = 1 | -1;

export interface RowCursorKeyEvent {
  key: string;
  target: EventTarget | null;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
}

/**
 * Elements that mean something else by these keys, and must keep them.
 *
 * ↑/↓ move a caret and change a `<select>`; Space types a space and activates a
 * button. Stealing them would change the chart under someone halfway through
 * typing a DSL expression — the worst version of this feature, because the
 * screen they were building moves while they are looking at the keyboard.
 *
 * Scoping the listener to the grid root already excludes the panel's own
 * surfaces — the filter bar, the column picker's search box, the builder — since
 * those are siblings of the grid, not descendants. This covers what AG Grid
 * renders *inside* the grid: floating filters, filter popovers, and the header
 * menu buttons, whose popup parent is the grid root by default.
 */
const SELF_HANDLING_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT", "OPTION", "BUTTON"]);

export function handlesOwnKeys(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (SELF_HANDLING_TAGS.has(target.tagName)) return true;
  // `closest`, not the element itself: a rich text surface puts focus on a child
  // of the editable host. `isContentEditable` would say this in one word but
  // jsdom does not implement it, which would make the case untestable.
  return target.closest('[contenteditable]:not([contenteditable="false"])') != null;
}

/**
 * What a keypress means for the row cursor, or `null` for "not ours".
 *
 * Space means *next*, same as ↓: it is the key a hand already resting on the
 * keyboard reaches for, and AG Grid Community's own binding for it — toggle the
 * focused row's selection — is actively wrong here. Toggling can *deselect*,
 * which leaves the grid with nothing highlighted while the chart still shows the
 * symbol that was selected a moment ago. The caller therefore takes Space away
 * from the grid *and* from the browser, whose default is to page the scroll
 * container the grid body is.
 */
export function rowCursorStep(event: RowCursorKeyEvent): RowStep | null {
  // A modifier means the user is asking the browser or the OS for something —
  // Cmd+↓ is end-of-document, Alt+↓ opens a native list, Shift+↓ extends a
  // selection. None of them mean "next row".
  if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return null;
  if (handlesOwnKeys(event.target)) return null;
  if (event.key === "ArrowDown" || event.key === " ") return 1;
  if (event.key === "ArrowUp") return -1;
  return null;
}

/**
 * Where the cursor lands, in *displayed* row indices, or `null` for nowhere.
 *
 * Two edges decided here rather than in the browser:
 *
 * - **No wrap.** At the last row ↓ does nothing. Wrapping would jump the chart
 *   back to the first symbol of a thousand-row screen and lose the user's place,
 *   and the keypress that did it is indistinguishable from the ones before it.
 * - **No cursor yet -> the top.** A first ↑ on a fresh grid lands on row one
 *   rather than being swallowed, so the feature answers the first key pressed.
 *   The same case covers a selected row that the filter has just hidden, whose
 *   index is `null`: the top is the only answer still on screen.
 */
export function stepRowIndex(current: number | null, count: number, step: RowStep): number | null {
  if (count <= 0) return null;
  if (current == null) return 0;
  // Clamped, because a row count can shrink under a held key: the server pushes
  // a full snapshot on every refresh and a filter can drop rows between two
  // repeats of the same keystroke.
  const from = Math.min(Math.max(current, 0), count - 1);
  const next = from + step;
  return next < 0 || next >= count ? null : next;
}
